import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import LoadingRing from '../ui-components/LoadingRing';
import type { MRIMachinePopulated } from '../room-overview/types';
import {
    getPublicMRIMachines,
    getAllMRIMachinesByDepartment,
    addMRIMachine,
    deleteMRIMachine,
} from '../../services/apiService';
import ScannerTemplatePicker from './ScannerTemplatePicker';
import ScannerForm from './ScannerForm';
import AddedScannersList from './AddedScannersList';

export default function MriScannerStep() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { toast } = useToast();

    const [templates, setTemplates] = useState<MRIMachinePopulated[]>([]);
    const [addedScanners, setAddedScanners] = useState<MRIMachinePopulated[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedTemplate, setSelectedTemplate] =
        useState<MRIMachinePopulated | null>(null);

    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            try {
                const [publicMachines, departmentMachines] = await Promise.all([
                    getPublicMRIMachines(),
                    getAllMRIMachinesByDepartment(),
                ]);
                setTemplates(
                    Array.isArray(publicMachines)
                        ? publicMachines.filter(
                              (m: MRIMachinePopulated) => m?._id
                          )
                        : []
                );
                setAddedScanners(
                    Array.isArray(departmentMachines)
                        ? departmentMachines
                        : []
                );
            } catch {
                toast({
                    title: t('onboarding.common.error'),
                    description: t('onboarding.scanners.toasts.loadError'),
                    variant: 'destructive',
                });
            } finally {
                setIsLoading(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAdd = async (name: string, coils: string) => {
        if (!selectedTemplate) return;

        setIsSaving(true);
        try {
            await addMRIMachine({
                Model: selectedTemplate.Model,
                Manufacturer: selectedTemplate.Manufacturer,
                Name: name,
                FieldStrength: selectedTemplate.FieldStrength,
                SpatialGradient: selectedTemplate.SpatialGradient || [],
                SlewRatePerAxis: selectedTemplate.SlewRatePerAxis,
                MaxSlewRate: selectedTemplate.MaxSlewRate,
                MaxSAR: selectedTemplate.MaxSAR || [],
                MaxB1rms: selectedTemplate.MaxB1rms,
                TRCoils: coils,
            });

            const updated = await getAllMRIMachinesByDepartment();
            setAddedScanners(Array.isArray(updated) ? updated : []);
            setSelectedTemplate(null);
            toast({ title: t('onboarding.scanners.toasts.added') });
        } catch {
            toast({
                title: t('onboarding.common.error'),
                description: t('onboarding.scanners.toasts.addError'),
                variant: 'destructive',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleRemove = async (machineId: string) => {
        try {
            await deleteMRIMachine(machineId);
            setAddedScanners((prev) =>
                prev.filter((m) => m._id !== machineId)
            );
        } catch {
            toast({
                title: t('onboarding.common.error'),
                description: t('onboarding.scanners.toasts.removeError'),
                variant: 'destructive',
            });
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center py-12">
                <LoadingRing size="large" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-lg font-semibold text-foreground">
                    {t('onboarding.scanners.title')}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                    {t('onboarding.scanners.description')}
                </p>
            </div>

            <ScannerTemplatePicker
                templates={templates}
                selectedId={selectedTemplate?._id ?? null}
                onSelect={setSelectedTemplate}
                onClear={() => setSelectedTemplate(null)}
            />

            {selectedTemplate && (
                <ScannerForm
                    key={selectedTemplate._id}
                    template={selectedTemplate}
                    isSaving={isSaving}
                    onAdd={handleAdd}
                />
            )}

            <AddedScannersList
                scanners={addedScanners}
                onRemove={handleRemove}
            />

            <div className="flex justify-between pt-4">
                <Button
                    variant="outline"
                    onClick={() => navigate('/wizard/users')}
                >
                    {t('onboarding.common.back')}
                </Button>
                <Button
                    variant={addedScanners.length > 0 ? 'default' : 'outline'}
                    onClick={() => navigate('/wizard/policies')}
                >
                    {addedScanners.length > 0
                        ? t('onboarding.common.saveAndContinue')
                        : t('onboarding.common.skipForNow')}
                </Button>
            </div>
        </div>
    );
}
